import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiTrash2, FiEdit2, FiArrowLeft, FiCheck } from "react-icons/fi";
import api from "../../utils/api";
import { useLanguages } from "../../hooks/useLanguages";
import Pagination from "../../components/common/Pagination";
import TableSearch from "../../components/common/TableSearch";
import { usePagination } from "../../hooks/usePagination";

const RolesManagement = () => {
  const { supportedLanguages } = useLanguages()
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeLang, setActiveLang] = useState("en");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [newRole, setNewRole] = useState({
    id: null,
    roleName: "",
    permissions: [128] // Default select Profile View
  });

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    paginatedData,
    totalItems,
    totalPages,
  } = usePagination(roles, ["role_name"]);

  useEffect(() => {
    fetchRolesAndPermissions();
  }, []);

  const fetchRolesAndPermissions = async () => {
    try {
      setLoading(true);
      setError("");
      const isStaff = window.location.pathname.includes("/restaurant-staff");
      const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
      const response = await api.get(`${apiPrefix}/staff/roles`);
      console.log("DEBUG: Raw API Response:", response.data);
      const data = response.data.data || response.data;
      console.log("DEBUG: Processed Data:", data);
      
      // Ensure we're working with arrays
      const fetchedRoles = Array.isArray(data.roles) ? data.roles : [];
      
      // Handle permissions - backend returns an object grouped by category
      let fetchedPermissions = [];
      if (data.permissions) {
        if (Array.isArray(data.permissions)) {
          fetchedPermissions = data.permissions;
        } else if (typeof data.permissions === 'object') {
          // Backend returns grouped permissions as object, flatten it into array
          Object.values(data.permissions).forEach(group => {
            if (Array.isArray(group)) {
              fetchedPermissions = fetchedPermissions.concat(group);
            }
          });
        }
      }
      
      console.log("DEBUG: Roles Count:", fetchedRoles.length);
      console.log("DEBUG: Permissions Count:", fetchedPermissions.length);
      console.log("DEBUG: Fetched Permissions:", fetchedPermissions);

      setRoles(fetchedRoles);
      setPermissions(fetchedPermissions);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setError("Failed to load roles and permissions");
    } finally {
      setLoading(false);
    }
  };

  const saveRole = async () => {
    if (!newRole.roleName.trim()) {
      setError("Role name is required");
      return;
    }

    if (newRole.permissions.length === 0) {
      setError("Please select at least one permission");
      return;
    }

    try {
      setError("");
      
      const payload = {
        roleName: newRole.roleName,
        permissions: JSON.stringify(newRole.permissions)
      };
      
      const isStaff = window.location.pathname.includes("/restaurant-staff");
      const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
      
      let response;
      if (newRole.id) {
        payload.roleId = newRole.id;
        response = await api.put(`${apiPrefix}/staff/update-role`, payload);
        setSuccess("Role updated successfully!");
      } else {
        response = await api.post(`${apiPrefix}/staff/add-role`, payload);
        setSuccess("Role created successfully!");
      }
      
      console.log("Save role response:", response);
      
      await fetchRolesAndPermissions();
      setShowCreateModal(false);
      resetNewRole();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error saving role:", error);
      setError(`Failed to ${newRole.id ? 'update' : 'create'} role: ` + (error.response?.data?.message || error.message));
    }
  };

  const resetNewRole = () => {
    setNewRole({
      id: null,
      roleName: "",
      permissions: [128] // Default select Profile View
    });
  };

  const togglePermission = (permissionId) => {
    const numId = Number(permissionId);
    
    setNewRole(prev => {
      let currentPerms = [...prev.permissions];
      const isSelecting = !currentPerms.includes(numId);
      
      if (isSelecting) {
        currentPerms.push(numId);
        
        // Auto-Selection Rules for Counter
        // If checking Counter Access (123), Takeaway (191), Dine-in (192), or QRS (193)
        if ([123, 191, 192, 193].includes(numId)) {
          // Auto select Menu View (173), Category View (179), Table View (6), and Order View (26)
          if (!currentPerms.includes(173)) currentPerms.push(173);
          if (!currentPerms.includes(179)) currentPerms.push(179);
          if (!currentPerms.includes(6)) currentPerms.push(6);
          if (!currentPerms.includes(26)) currentPerms.push(26);
          
          // Auto select Base Counter Access (123) if a sub-permission is checked
          if (numId !== 123 && !currentPerms.includes(123)) currentPerms.push(123);
        }
      } else {
        // Deselecting
        currentPerms = currentPerms.filter(id => Number(id) !== numId);
        
        // If deselecting Base Counter Access (123), remove ALL counter-related stuff
        if (numId === 123) {
          currentPerms = currentPerms.filter(id => ![191, 192, 193, 173, 179, 6, 26].includes(id));
        } else if ([191, 192, 193].includes(numId)) {
          // If they deselect a sub-permission, check if any sub-permissions are left
          const hasSubPerms = currentPerms.some(id => [191, 192, 193].includes(id));
          if (!hasSubPerms) {
            // If no sub-permissions are left, also remove base 123 and the dependencies
            currentPerms = currentPerms.filter(id => ![123, 173, 179, 6, 26].includes(id));
          } else {
            // If they specifically unchecked Dine-in (192), remove Table View (6)
            if (numId === 192) {
              currentPerms = currentPerms.filter(id => id !== 6);
            }
          }
        }
      }
      
      return { ...prev, permissions: currentPerms };
    });
  };

  const getPermissionLabel = (permission, language = activeLang) => {
    if (!permission) return "Unknown Permission";
    return permission.translations?.[language]?.label || permission.label || permission.screen || "No Label";
  };

  const getPermissionDescription = (permission, language = activeLang) => {
    if (!permission) return "No description available";
    return permission.translations?.[language]?.description || permission.description || permission.screen || "No description available";
  };

  const deleteRole = async (roleId) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        setError("");
        // Call the delete API
        const isStaff = window.location.pathname.includes("/restaurant-staff");
        const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
        await api.post(`${apiPrefix}/staff/delete-roles`, {
          roleIds: [roleId]
        });
        // Refresh the list after successful deletion
        await fetchRolesAndPermissions();
        setSuccess("Role deleted successfully!");
        setTimeout(() => setSuccess(""), 3000);
      } catch (error) {
        console.error("Error deleting role:", error);
        setError("Failed to delete role: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const editRole = (role) => {
    // For now, we'll just pre-fill the create modal with role data
    const rawPerms = Array.isArray(role.permissions) ? role.permissions : 
                  (typeof role.permissions === 'string' ? JSON.parse(role.permissions) : []);
    setNewRole({
      id: role.id,
      roleName: role.role_name || role.name || "",
      permissions: rawPerms.map(p => Number(p))
    });
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button 
          className="text-2xl text-gray-600 hover:text-gray-800 transition-colors"
          onClick={() => navigate(-1)}
        >
          <FiArrowLeft />
        </button>
        <h1 className="text-xl font-bold text-gray-800">Roles & Permissions</h1>
        <button 
          onClick={() => {
            resetNewRole();
            setShowCreateModal(true);
          }}
          className="text-2xl text-primary hover:text-red-700 transition-colors"
        >
          <FiPlus />
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Language Switcher */}
      <div className="flex space-x-2 mb-4">
        {Array.isArray(supportedLanguages) && supportedLanguages.map((lang) => (
          <button
            key={lang}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium border transition-colors ${
              activeLang === lang
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300"
            }`}
          >
            {lang.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Permissions Count & Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <p className="text-gray-700 text-sm font-semibold">
          <span className="text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200 mr-2">
            {Array.isArray(permissions) ? permissions.length : 0}
          </span>
          Permissions Available
        </p>
        <TableSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search roles by name..." />
      </div>

      {/* Roles List */}
      <div className="space-y-4">
        {!Array.isArray(paginatedData) || paginatedData.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <FiPlus className="text-3xl text-gray-400" />
            </div>
            <p className="text-gray-500 mb-2 text-lg">
              {searchTerm ? "No roles found matching your search" : "No roles defined yet"}
            </p>
            {!searchTerm && (
              <>
                <p className="text-gray-400 text-sm mb-6">
                  Create your first role to start managing staff permissions
                </p>
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary text-white px-8 py-3 rounded-full hover:bg-red-700 transition-colors font-medium"
                >
                  Create First Role
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-100">
            <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-4">Role Name</th>
                  <th className="px-6 py-4">Permissions Overview</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedData.map((role) => {
                  const rolePermissions = Array.isArray(role.permissions) 
                    ? role.permissions 
                    : (typeof role.permissions === 'string' ? JSON.parse(role.permissions) : []);
                  
                  return (
                    <tr 
                      key={role.id} 
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => editRole(role)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-gray-900 truncate">
                          {role.role_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md border border-blue-100 font-medium">
                          {rolePermissions.length} rules assigned
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteRole(role.id);
                            }}
                            title="Delete role"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {roles.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateModal && (
        <CreateRoleModal
          newRole={newRole}
          permissions={Array.isArray(permissions) ? permissions : []}
          activeLang={activeLang}
          supportedLanguages={Array.isArray(supportedLanguages) ? supportedLanguages : []}
          onUpdateField={(field, value) => setNewRole(prev => ({ ...prev, [field]: value }))}
          onTogglePermission={togglePermission}
          onSetActiveLang={setActiveLang}
          getPermissionLabel={getPermissionLabel}
          getPermissionDescription={getPermissionDescription}
          onCreate={saveRole}
          onClose={() => {
            setShowCreateModal(false);
            resetNewRole();
            setError("");
          }}
          error={error}
        />
      )}
    </div>
  );
};

// Role Card Component
const RoleCard = ({ role, permissions, activeLang, getPermissionLabel, onEdit, onDelete }) => {
  // Parse the stringified permissions array
  const parsePermissions = (permString) => {
    if (!permString) return [];
    try {
      if (typeof permString === 'string') {
        return JSON.parse(permString);
      }
      return permString;
    } catch (error) {
      console.error("Error parsing permissions:", error);
      return [];
    }
  };

  const rolePermissions = parsePermissions(role.permissions);
  
  return (
    <div className="bg-white shadow-lg rounded-xl p-4 border border-gray-200 hover:shadow-xl transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-800 mb-1">{role.role_name || role.name || "Unnamed Role"}</h3>
          <p className="text-sm text-gray-500">
            {rolePermissions.length} permissions assigned
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            className="text-blue-600 hover:text-blue-800 p-2 transition-colors"
            onClick={onEdit}
            title="Edit role"
          >
            <FiEdit2 size={18} />
          </button>
          <button 
            className="text-red-600 hover:text-red-800 p-2 transition-colors"
            onClick={onDelete}
            title="Delete role"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      </div>
      
      {/* Permissions List */}
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Assigned Permissions:</h4>
        <div className="flex flex-wrap gap-2">
          {rolePermissions.length > 0 ? (
            rolePermissions.map(permId => {
              const permission = Array.isArray(permissions) ? permissions.find(p => String(p.id) === String(permId)) : null;
              return permission ? (
                <span 
                  key={permId}
                  className="bg-green-50 text-green-700 text-xs px-3 py-1.5 rounded-full border border-green-200 flex items-center gap-1"
                  title={getPermissionLabel(permission)}
                >
                  <FiCheck size={12} />
                  {getPermissionLabel(permission, activeLang)}
                </span>
              ) : (
                <span 
                  key={permId}
                  className="bg-gray-100 text-gray-500 text-xs px-3 py-1.5 rounded-full border border-gray-200"
                >
                  Permission ID: {permId}
                </span>
              );
            })
          ) : (
            <p className="text-gray-400 text-sm">No permissions assigned</p>
          )}
        </div>
      </div>
    </div>
  );
};



// Create Role Modal Component
const CreateRoleModal = ({
  newRole,
  permissions,
  activeLang,
  supportedLanguages,
  onUpdateField,
  onTogglePermission,
  onSetActiveLang,
  getPermissionLabel,
  getPermissionDescription,
  onCreate,
  onClose,
  error
}) => {
  // More robust permission grouping
  const getGroupedPermissions = () => {
    if (!permissions || permissions.length === 0) return {};

    const groups = {
      "Business": [131],
      "Management - Add Menu": [171],
      "Management - All Menu": [173, 175, 177],
      "Management - Menus Category": [179, 181, 183, 185],
      "Management - Tables": [6, 7, 8, 9],
      "Management - Inventory": [10, 11, 12, 13],
      "Management - Customer": [133, 135, 137, 139],
      "Management - Vendor": [141, 143, 145, 147],
      "Management - Staff": [14, 15, 16, 17],
      "Management - Expense / Income": [18, 19, 20, 21],
      "Help Chat": [149],
      "Apps - Counter Access": [123],
      "Apps - KOT - Display": [22, 23, 24, 25],
      "Apps - Delivery / Dispatch": [187, 188, 189, 190],
      "Apps - Receipt": [153, 155],
      "Apps - Orders": [26, 27, 28, 29],
      "Settings - Language": [157],
      "Floating Navigation": [163, 159, 165, 167, 161, 169],
      "Basic Access": [1, 117, 118, 128]
    };

    const categorized = {};
    const usedIds = new Set();
    const allGroupedIds = Object.values(groups).flat();

    // Categorize remaining into "General Permissions" first so it renders at the top
    const remaining = permissions.filter(p => !allGroupedIds.includes(Number(p.id)));
    if (remaining.length > 0) {
      categorized["General Permissions"] = remaining;
      remaining.forEach(item => usedIds.add(Number(item.id)));
    }

    // Then categorize based on hardcoded mapping
    Object.entries(groups).forEach(([name, ids]) => {
      const items = permissions.filter(p => ids.includes(Number(p.id)));
      if (items.length > 0) {
        categorized[name] = items;
        items.forEach(item => usedIds.add(Number(item.id)));
      }
    });

    return categorized;
  };

  const groupedPermissions = getGroupedPermissions();
  const roleName = newRole.roleName || "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">{newRole.id ? "Edit Role" : "Create New Role"}</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Language Switcher */}
        <div className="flex space-x-2 mb-4">
          {Array.isArray(supportedLanguages) && supportedLanguages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => onSetActiveLang(lang)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium border transition-colors ${
                activeLang === lang
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300"
              }`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Role Name */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Role Name *
          </label>
          <input
            type="text"
            placeholder="Enter role name (e.g., Manager, Waiter, Cashier)"
            value={roleName}
            onChange={(e) => onUpdateField("roleName", e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Permissions Selection */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-800">Select Permissions *</h3>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {Array.isArray(newRole.permissions) ? newRole.permissions.length : 0} selected
            </span>
          </div>
          
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {Object.keys(groupedPermissions).length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No permissions available to display.
              </div>
            ) : (
              Object.entries(groupedPermissions).map(([category, categoryPermissions]) => {
                if (
                  category === "Apps - Takeaway Access" || 
                  category === "Apps - QRS Access" ||
                  category === "General Permissions"
                ) return null;
                
                return (
                <div key={category} className="border-b border-gray-200 last:border-b-0">
                  <div className="bg-gray-50 px-4 py-2">
                    <h4 className="font-medium text-gray-700 text-sm">{category}</h4>
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoryPermissions.map(permission => (
                      <label 
                        key={permission.id} 
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                          Array.isArray(newRole.permissions) && newRole.permissions.includes(permission.id)
                            ? "bg-blue-50 border-blue-200"
                            : "bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={Array.isArray(newRole.permissions) && newRole.permissions.includes(permission.id)}
                          onChange={() => onTogglePermission(permission.id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <span className={`text-sm font-medium block ${
                            Array.isArray(newRole.permissions) && newRole.permissions.includes(permission.id) 
                              ? "text-blue-700" 
                              : "text-gray-800"
                          }`}>
                            {getPermissionLabel(permission, activeLang)}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {getPermissionDescription(permission, activeLang)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )})
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onCreate}
            disabled={!roleName.trim() || !Array.isArray(newRole.permissions) || newRole.permissions.length === 0}
            className="flex-1 py-3 bg-primary text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {newRole.id ? "Update Role" : "Create Role"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolesManagement;